import React from 'react';
import {
  ResponsiveContainer,
  RadarChart as RechartsRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
} from 'recharts';
import type { RadarPoint } from '@/utils/diagnosisEngine';

interface RadarChartProps {
  radarData: RadarPoint[];
  overallScore: number;
  percentile: string;
  trackName: string;
  trackBadge: string;
  costKrw?: number;
  elapsedMs?: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: RadarPoint;
  }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const diff = data.score - data.marketAvg;
    return (
      <div className="bg-slate-900/95 text-white p-3.5 rounded-xl shadow-xl text-xs backdrop-blur border border-slate-700 min-w-[200px]">
        <p className="font-extrabold text-sm text-indigo-300 mb-1.5">{data.axis}</p>
        <div className="flex justify-between items-center py-0.5">
          <span className="text-slate-400">내 현재 역량 스탯:</span>
          <span className="font-bold text-white text-sm">{data.score}점</span>
        </div>
        <div className="flex justify-between items-center py-0.5">
          <span className="text-slate-400">상위 20% 시장 합격선:</span>
          <span className="text-slate-300 font-semibold">{data.marketAvg}점</span>
        </div>
        <div className="mt-2 pt-2 border-t border-slate-800 flex justify-between items-center">
          <span className="text-slate-400">합격선 대비:</span>
          <span
            className={`font-extrabold ${
              diff >= 0 ? 'text-emerald-400' : 'text-amber-400'
            }`}
          >
            {diff >= 0 ? `+${diff}점 우위` : `${diff}점 보완 필요`}
          </span>
        </div>
        <p className="text-[11px] text-slate-300 mt-2 leading-relaxed bg-white/5 p-2 rounded-lg">
          {data.comment}
        </p>
      </div>
    );
  }
  return null;
};

export default function RadarChart({
  radarData,
  overallScore,
  percentile,
  trackName,
  trackBadge,
}: RadarChartProps) {
  // Find the weakest skill axis that needs improvement most
  const weakestPoint = [...radarData].sort(
    (a, b) => (a.score - a.marketAvg) - (b.score - b.marketAvg)
  )[0];

  const gap = weakestPoint ? weakestPoint.score - weakestPoint.marketAvg : 0;

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200/90 dark:border-gray-800 rounded-3xl p-6 md:p-8 shadow-sm transition-all space-y-8">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-gray-100 dark:border-gray-800">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-brand-50 text-brand-700 border border-brand-200 dark:bg-brand-950/50 dark:text-brand-300 dark:border-brand-800">
              {trackBadge}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-bold">
              <i className="ri-checkbox-circle-fill text-sm"></i>
              실제 채용공고 4,012건 실측 대조 완료
            </span>
          </div>
          <h3 className="text-xl md:text-2xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight">
            {trackName} 듀얼 역량 레이더 스탯
          </h3>
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            [내 현재 스탯] vs [목표 직군 상위 20% 시장 합격선] 겹침 대조 시각화
          </p>
        </div>

        {/* Score Card */}
        <div className="flex items-center gap-4 bg-gradient-to-br from-indigo-50/90 to-purple-50/60 dark:from-indigo-950/40 dark:to-purple-950/20 border border-indigo-100 dark:border-indigo-900/50 rounded-2xl px-5 py-3.5 shrink-0 shadow-sm">
          <div className="text-right">
            <span className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider block">
              내 종합 역량 지수
            </span>
            <span className="text-3xl font-black text-indigo-700 dark:text-indigo-300 leading-none">
              {overallScore}
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-0.5">/100</span>
            </span>
          </div>
          <div className="h-10 w-px bg-indigo-200 dark:bg-indigo-800/60" />
          <div>
            <span className="inline-block px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-xs font-black shadow-sm">
              {percentile}
            </span>
            <span className="text-[11px] text-gray-500 dark:text-gray-400 block mt-1 font-medium">
              채용 매칭 구간
            </span>
          </div>
        </div>
      </div>

      {/* Dual Radar Chart Visualization */}
      <div className="py-2">
        <div className="h-68 sm:h-80 md:h-96 w-full relative">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsRadarChart
              data={radarData}
              cx="50%"
              cy="50%"
              outerRadius="75%"
              margin={{ top: 10, right: 30, bottom: 10, left: 30 }}
            >
              <PolarGrid
                stroke="#cbd5e1"
                strokeDasharray="3 3"
                className="dark:stroke-gray-700"
              />
              <PolarAngleAxis
                dataKey="axis"
                tick={{ fill: '#334155', fontSize: 13, fontWeight: 700 }}
              />
              <PolarRadiusAxis
                angle={30}
                domain={[0, 100]}
                tick={{ fill: '#94a3b8', fontSize: 11 }}
              />
              <Tooltip content={<CustomTooltip />} />
              {/* Benchmark: Top 20% Market Pass Line (Grey Dashed Line) */}
              <Radar
                name="목표 직군 상위 20% 시장 합격선"
                dataKey="marketAvg"
                stroke="#94a3b8"
                strokeWidth={2}
                strokeDasharray="5 5"
                fill="#cbd5e1"
                fillOpacity={0.25}
              />
              {/* User Current Stats (Blue / Indigo Solid Line) */}
              <Radar
                name="내 현재 역량 스탯"
                dataKey="score"
                stroke="#4f46e5"
                strokeWidth={3}
                fill="#6366f1"
                fillOpacity={0.55}
              />
            </RechartsRadarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 rounded-full bg-indigo-600 ring-2 ring-indigo-200 dark:ring-indigo-900 shadow-sm" />
            <span className="font-bold text-gray-900 dark:text-gray-100">
              내 현재 스탯 (블루)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 rounded-full bg-slate-300 border-2 border-dashed border-slate-500" />
            <span className="font-medium text-gray-600 dark:text-gray-400">
              목표 직군 상위 20% 시장 합격선 (회색 점선)
            </span>
          </div>
        </div>
      </div>

      {/* 5-Axis Score Breakdown Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 pt-6 border-t border-gray-100 dark:border-gray-800">
        {radarData.map((pt) => {
          const diff = pt.score - pt.marketAvg;
          return (
            <div
              key={pt.axis}
              className="bg-gray-50/80 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/60 rounded-2xl p-3.5 flex flex-col justify-between hover:border-brand-300 dark:hover:border-brand-700 transition-colors"
            >
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold block truncate">
                  {pt.axis}
                </span>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-xl font-black text-gray-900 dark:text-gray-100">
                    {pt.score}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">점</span>
                </div>
              </div>
              <div className="mt-3 pt-2.5 border-t border-gray-200/60 dark:border-gray-700/50 flex items-center justify-between">
                <span
                  className={`text-xs font-extrabold ${
                    diff >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`}
                >
                  {diff >= 0 ? `+${diff}점 우위` : `${diff}점 부족`}
                </span>
                <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                  기준 {pt.marketAvg}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* 부족 역량 보완을 위한 실제 학원 커리큘럼(9/11 시간표 실시간 연동) 카드 직결 */}
      <div className="rounded-3xl border-2 border-brand-200/90 dark:border-brand-800/90 bg-gradient-to-br from-brand-50/60 via-white to-indigo-50/40 dark:from-gray-900 dark:via-gray-900/90 dark:to-brand-950/40 p-6 sm:p-7 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-brand-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-lg bg-red-100 dark:bg-red-950/60 text-red-700 dark:text-red-300 text-xs font-black">
              역량 갭(Gap) 보완 추천
            </span>
            <h4 className="text-base sm:text-lg font-black text-gray-900 dark:text-gray-100">
              부족 역량 보완 학원 커리큘럼 직결
            </h4>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-700 dark:text-brand-300 bg-white dark:bg-gray-800 px-3 py-1 rounded-full border border-brand-200 dark:border-brand-700">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            9/11 개강 시간표 실시간 연동중
          </span>
        </div>

        {weakestPoint && (
          <div className="py-4">
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed break-keep">
              현재 <strong className="text-brand-700 dark:text-brand-400 font-black">{trackName}</strong> 직군 합격선 대비{' '}
              <span className="inline-block px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-200 font-extrabold text-xs">
                [{weakestPoint.axis}] 역량이 {Math.abs(gap)}점 부족
              </span>
              합니다. 이 영역을 1:1로 메꿀 수 있는 정부 승인 정규 커리큘럼 시간표입니다.
            </p>
          </div>
        )}

        {/* 9/11 시간표 실시간 연동 커리큘럼 카드 */}
        <div className="bg-white dark:bg-gray-850 rounded-2xl p-5 border border-brand-100 dark:border-gray-800 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded text-[11px] font-extrabold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                  자부담 0원 전액지원 대상
                </span>
                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  K-디지털 아카데미
                </span>
              </div>
              <h5 className="text-base sm:text-lg font-black text-gray-900 dark:text-gray-100 mt-1.5">
                [실무 특화] AI 비즈니스 실무 캡스톤 & 에이전트 포트폴리오 완성반
              </h5>
            </div>
            <div className="text-left md:text-right shrink-0">
              <span className="text-xs text-gray-400 line-through">정규 4,800,000원</span>
              <p className="text-lg font-black text-brand-600 dark:text-brand-400">
                실부담 0원 <span className="text-xs font-normal text-gray-500">(1유형/K디지털)</span>
              </p>
            </div>
          </div>

          {/* 9/11 시간표 스펙 상세 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs bg-gray-50 dark:bg-gray-800/60 p-3.5 rounded-xl border border-gray-100 dark:border-gray-700/50">
            <div>
              <span className="text-gray-500 dark:text-gray-400 block font-medium">📅 9/11 연동 개강일</span>
              <span className="font-extrabold text-gray-900 dark:text-gray-100 mt-0.5 block">
                9월 11일(수) 주간반 / 10월 주말반
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400 block font-medium">⏰ 교육 시간표</span>
              <span className="font-extrabold text-gray-900 dark:text-gray-100 mt-0.5 block">
                평일 09:30 ~ 18:30 (1일 8시간)
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400 block font-medium">🎁 훈련장려금 혜택</span>
              <span className="font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5 block">
                월 최대 31.6만원 현금 지급
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              * 출석률 80% 이상 시 수료증 발급 및 고용순증 기업 132곳 1:1 매칭 연계
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <a
                href="/과정비교"
                className="flex-1 sm:flex-none text-center px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 text-xs font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                시간표 전수 비교
              </a>
              <a
                href="#문의"
                className="flex-1 sm:flex-none text-center px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-xs font-extrabold text-white shadow-sm transition-all"
              >
                9/11 시간표 자부담 0원 신청
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
